/**
 * Network Status Hook
 * 
 * Monitors network connectivity and connection quality
 * Provides real-time network status for UI feedback
 */

import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  effectiveType: ConnectionEffectiveType | null;
  downlink: number | null;
  rtt: number | null;
}

type ConnectionEffectiveType = 'slow-2g' | '2g' | '3g' | '4g';

/**
 * Hook to monitor network status
 * 
 * @returns Network status object with isOnline, isSlowConnection, effectiveType, etc.
 */
export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(
    typeof window !== 'undefined' ? navigator.onLine : true
  );
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [effectiveType, setEffectiveType] = useState<ConnectionEffectiveType | null>(null);
  const [downlink, setDownlink] = useState<number | null>(null);
  const [rtt, setRtt] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Check connection type if available (Network Information API)
    const connection = navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection;
    
    const updateConnectionStatus = () => {
      if (connection) {
        const type = connection.effectiveType;
        setEffectiveType(type);
        setIsSlowConnection(type === 'slow-2g' || type === '2g');
        setDownlink(connection.downlink || null);
        setRtt(connection.rtt || null);
      }
    };
    
    if (connection) {
      updateConnectionStatus();
      connection.addEventListener('change', updateConnectionStatus);
    }
    
    // Fallback to online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      if (connection) {
        updateConnectionStatus();
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setIsSlowConnection(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Initial check
    updateConnectionStatus();
    
    return () => {
      if (connection) {
        connection.removeEventListener('change', updateConnectionStatus);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    isOnline,
    isSlowConnection,
    effectiveType,
    downlink,
    rtt,
  };
}
